"use client";

import Image from "next/image";
import Link from "next/link";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { FaTwitter, FaDiscord, FaMediumM, FaYoutube } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";

import { Markdown } from "../atoms/Markdown";
import Images from "styles/image-library";

import { useMobileFooterConfig } from "./footer.config";

const socialLinks = [
  {
    link: "https://discord.gg/yTdH8YkYKg",
    icon: <FaDiscord />,
  },
  {
    link: "https://twitter.com/MarinadeFinance",
    icon: <FaTwitter />,
  },
  {
    link: "https://medium.com/marinade-finance",
    icon: <FaMediumM />,
  },
  {
    link: "https://www.youtube.com/channel/UC3sarW4IBiJZR36laQUFgYg",
    icon: <FaYoutube />,
  },
];

interface FooterProps {
  className?: string;
}

const Footer: FunctionComponent<FooterProps> = ({ className }) => {
  const config = useMobileFooterConfig();
  const { t } = useTranslation();

  return (
    <div
      id="footer"
      className={`hidden md:block flex-col font-gantari bg-white width-full px-4 lg:px-[156px] lg:px-160 pt-[72px] gap-12 pb-[80px] md:pb-0 ${className}`}
      aria-label="footer-section"
    >
      <div className="flex justify-between flex-col md:flex-row gap-6 flex-wrap">
        <div className="flex flex-col min-width-30 gap-6">
          <Link href="/" className="cursor-pointer w-[200px] h-[40px]">
            <Image
              src={Images.marinade_logo_black}
              alt="Marinade Logo"
              width={200}
              height={40}
            />
          </Link>
          <Markdown
            className="text-sm md:text-base font-normal text-tertiary-text max-w-[360px] md:max-w-[30vw] lg:max-w-[25vw]"
            text={t("indexPage.marinade-description")}
          />
          <div className="pb-8 flex flex-col gap-4">
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <Link
                  key={social.link}
                  href={social.link}
                  className="text-tertiary-text"
                >
                  {social.icon}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {config.map((cat) => (
          <div key={cat.title} className="flex flex-col">
            <span className="pb-4 text-md md:text-xl text-overlay-bg font-semibold">
              {cat.title}
            </span>
            {cat.items.map((item) => (
              <Link
                key={item.title}
                href={item.route}
                passHref={item.external}
                className="flex text-sm md:text-base text-tertiary-text font-normal hover:underline gap-2 py-[3px]"
              >
                {item.title}
                {item.external && <FiExternalLink />}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <div className="flex flex-col-reverse md:flex-row justify-between width-full my-6 text-xs md:text-sm font-normal text-secondary-text gap-4 flex-wrap">
        <span className="opacity-50">
          {t("indexPage.footer-copyright")?.replace(
            "{{year}}",
            new Date().getFullYear().toString()
          )}
        </span>
        <span className="py-[3px]">
          <Link
            href="https://marinade.finance/terms"
            className="hover:underline"
          >
            {t("indexPage.terms")}
          </Link>
          <Link
            href="https://marinade.finance/privacy-policy"
            className="ml-4 hover:underline"
          >
            {t("indexPage.privacy")}
          </Link>
        </span>
      </div>
    </div>
  );
};

export default Footer;
